/**
 * Programs (Training Plans) API - Client-Side Service
 * 
 * All program database operations go through server-side Express API.
 * Client-side code MUST NOT access the database directly.
 */
import type { Program } from '../types/program-types';
import { apiFetch, pollEndpoint } from '@/lib/api-client';

// Response types
interface CreateProgramResponse {
  success: boolean;
  programId: string;
  message: string;
}

interface UpdateResponse {
  success: boolean;
  message: string;
}

/**
 * Create a new program
 */
export async function createProgram(programData: Omit<Program, 'id'>): Promise<string> {
  const response = await apiFetch<CreateProgramResponse>('/programs', {
    method: 'POST',
    body: JSON.stringify(programData),
  });
  return response.programId;
}

/**
 * Get a single program by ID
 */
export async function getProgram(programId: string): Promise<Program | null> {
  try {
    return await apiFetch<Program>(`/programs/${programId}`);
  } catch (error: any) {
    if (error.message?.includes('not-found') || error.message?.includes('404')) {
      return null;
    }
    throw error;
  }
}

/**
 * Get all programs for the current user
 */
export async function getPrograms(clientId?: string): Promise<Program[]> {
  const query = clientId ? `?clientId=${clientId}` : '';
  return apiFetch<Program[]>(`/programs${query}`);
}

/**
 * Update a program
 */
export async function updateProgram(
  programId: string,
  updates: Partial<Program>
): Promise<void> {
  await apiFetch<UpdateResponse>(`/programs/${programId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

/**
 * Delete a program
 */
export async function deleteProgram(programId: string): Promise<void> {
  await apiFetch<UpdateResponse>(`/programs/${programId}`, {
    method: 'DELETE',
  });
}

/**
 * Duplicate a program (creates a copy for the current user)
 */
export async function duplicateProgram(
  programId: string,
  newName?: string
): Promise<string> {
  const response = await apiFetch<CreateProgramResponse>(
    `/programs/${programId}/duplicate`,
    {
      method: 'POST',
      body: JSON.stringify({ newName }),
    }
  );
  return response.programId;
}

/**
 * Subscribe to programs with polling for real-time updates
 * Returns cleanup function to stop polling
 */
export function subscribeToPrograms(
  callback: (programs: Program[]) => void,
  clientId?: string,
  intervalMs: number = 5000,
  onError?: (error: Error) => void
): () => void {
  const endpoint = clientId ? `/programs?clientId=${clientId}` : '/programs';
  return pollEndpoint<Program[]>(endpoint, callback, intervalMs, onError);
}
